import { IButtonStyles, ITextStyles } from '@fluentui/react';
import { IPIconButton, IPText } from 'baseComponents';
import { IPStack } from 'baseComponents/Stack/IPStack';
import {
  cancelIcon,
  globalFontBoldWeight,
  globalPillSelectedColor,
  globalStackTokensGapExtraSmall,
} from 'globalStyles';
import { getBestForGroundColor } from 'utils/colorHelpers';

export interface ISingleTag {
  tagId: string | number;
  tagName: string;
  tagColor: string;
  tagData?: string | number | boolean;
}

export const getInfoTag = (id: string | number, text: string, darkMode: boolean): ISingleTag => {
  return {
    tagId: id,
    tagName: text,
    tagColor: darkMode ? '#1e7d49' : '#42db87',
  };
};

interface ISingleTagProps {
  tag: ISingleTag;
  compact?: boolean;
  bold?: boolean;
  selected?: boolean;
  padding?: number;
  width?: number;
  minWidth?: number;
  maxWidth?: number; //set this to set a maximum width for example when the parent container has a maximum size
  onRemove?: (item: ISingleTag) => void;
  onClick?: (item: ISingleTag) => void;
}

export const SingleTag = (props: ISingleTagProps) => {
  const getTagStyles = (tag: ISingleTag) => {
    return {
      root: {
        background: props.selected ? globalPillSelectedColor : tag.tagColor,
        border: `1px solid ${tag.tagColor}`,
        borderRadius: '10px',
        margin: '2px',
        height: props.compact ? '18px' : '22px',
        width: props.width,
        minWidth: props.minWidth,
        maxWidth: props.maxWidth,
        cursor: props.onClick ? 'pointer' : undefined,
        padding: props.padding,
      },
    };
  };

  const getTagTextStyles = (tag: ISingleTag, bold?: boolean): ITextStyles => {
    return {
      root: {
        marginLeft: '5px',
        marginRight: '5px',
        whiteSpace: 'nowrap',
        fontWeight: bold ? globalFontBoldWeight : undefined,
        color: getBestForGroundColor(tag),
      },
    };
  };

  const tagButtonStyles: IButtonStyles = {
    root: {
      borderRadius: '8px',
      height: props.compact ? '18px' : '22px',
      width: props.compact ? '18px' : '22px',
    },
  };

  return (
    <IPStack
      tabIndex={props.onClick ? 0 : undefined}
      horizontal
      styles={getTagStyles(props.tag)}
      tokens={globalStackTokensGapExtraSmall}
      verticalAlign="center"
      horizontalAlign="space-between"
      onClick={() => props.onClick?.(props.tag)}
      onKeyDown={(ev) => {
        if (ev.key === 'Enter' || ev.key === 'Space') {
          props.onClick?.(props.tag);
        }
      }}
    >
      <IPStack.Item style={{ overflow: 'hidden' }}>
        <IPText
          block
          nowrap
          styles={getTagTextStyles(props.tag, props.bold)}
          key={props.tag.tagId.toString()}
          variant={props.compact ? 'small' : 'medium'}
        >
          {props.tag.tagName}
        </IPText>
      </IPStack.Item>
      {props.onRemove && (
        <IPStack.Item>
          <IPIconButton
            onClick={() => {
              if (props.onRemove) {
                props.onRemove(props.tag);
              }
            }}
            styles={tagButtonStyles}
            iconProps={cancelIcon}
          />
        </IPStack.Item>
      )}
    </IPStack>
  );
};

//
// SingleTags component to render multiple tags in a stack
//
interface ISingleTagsProps {
  compact?: boolean;
  tags: ISingleTag[];
  onRemove?: (item: ISingleTag) => void;
  maxWidth?: number;
}

export const SingleTags = (props: ISingleTagsProps) => {
  return (
    <IPStack horizontal wrap tokens={globalStackTokensGapExtraSmall}>
      {props.tags?.map((_tag, index) => {
        return (
          <IPStack.Item key={'tag' + index.toString()} styles={{ root: { margin: 0 } }}>
            <SingleTag tag={_tag} onRemove={props.onRemove} compact={props.compact} maxWidth={props.maxWidth} />
          </IPStack.Item>
        );
      })}
    </IPStack>
  );
};
